import { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Lock,
  Upload,
  FileText,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { MaskedLines } from "./MaskedText";
import Field from "./Field";
import { api, extractErrorMessage } from "@/lib/api";

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";

function DropField({ label, file, onFile, required, testId }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const handleFiles = (f) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error(`${label}: file must be under 5MB`);
      return;
    }
    if (!ACCEPT.split(",").includes(f.type)) {
      toast.error(`${label}: only JPG, PNG, WEBP or PDF`);
      return;
    }
    onFile(f);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow text-[color:var(--sbh-ink-2)]">
          {label}
          {required ? <span className="text-[color:var(--sbh-terracotta)]"> *</span> : null}
        </span>
        {file ? (
          <button
            type="button"
            onClick={() => {
              onFile(null);
              if (inputRef.current) inputRef.current.value = "";
            }}
            className="text-[11px] tracking-[0.16em] uppercase text-[color:var(--sbh-muted)] hover:text-[color:var(--sbh-terracotta)] transition-colors flex items-center gap-1"
            data-testid={`${testId}-remove`}
          >
            <X className="w-3 h-3" /> Remove
          </button>
        ) : null}
      </div>
      <div
        className={`dropzone ${drag ? "filled" : ""} ${file ? "filled" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          handleFiles(e.dataTransfer.files?.[0]);
        }}
        onClick={() => inputRef.current?.click()}
        data-testid={testId}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleFiles(e.target.files?.[0])}
          data-testid={`${testId}-input`}
        />
        {file ? (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[color:var(--sbh-terracotta)] flex items-center justify-center">
              <FileText className="w-4 h-4 text-[color:var(--sbh-terracotta)]" strokeWidth={1.6} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate font-body text-[14px] text-[color:var(--sbh-ink)]">
                {file.name}
              </div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--sbh-muted)]">
                {(file.size / 1024).toFixed(0)} KB · {file.type.split("/").pop()}
              </div>
            </div>
            <Check className="w-5 h-5 text-[color:var(--sbh-terracotta)]" strokeWidth={2} />
          </div>
        ) : (
          <div className="flex items-center gap-3 text-[color:var(--sbh-ink-2)]">
            <div className="w-9 h-9 border border-dashed border-[color:var(--sbh-border)] flex items-center justify-center">
              <Upload className="w-4 h-4" strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <div className="text-[14px]">Drop file here or click to upload</div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-[color:var(--sbh-muted)]">
                JPG · PNG · PDF · up to 5MB
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Slide4Address({
  onSubmitted,
  onBack,
  business,
  value,
  onChange,
  resumeToken,
}) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const [submitting, setSubmitting] = useState(false);

  const [panFile, setPanFile] = useState(null);
  const [chequeFile, setChequeFile] = useState(null);
  const [gstFile, setGstFile] = useState(null);

  const gstYes = !!value.gst_registered;

  const handleSubmit = async () => {
    const miss = [];
    if (!value.address_line_1) miss.push("Address line 1");
    if (!value.district) miss.push("District");
    if (!value.state) miss.push("State");
    if (!/^[1-9][0-9]{5}$/.test((value.pin_code || "").trim()))
      miss.push("Valid 6-digit pin code");
    const pan = (value.pan_number || "").trim().toUpperCase();
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) miss.push("Valid PAN (AAAAA0000A)");
    if (gstYes) {
      const gstin = (value.gstin || "").trim().toUpperCase();
      if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/.test(gstin))
        miss.push("Valid 15-character GSTIN");
    }
    if (!panFile) miss.push("PAN document upload");
    if (!chequeFile) miss.push("Cancelled cheque / passbook upload");
    if (gstYes && !gstFile) miss.push("GST certificate upload");
    if (miss.length) {
      toast.error(`Please complete: ${miss.join(", ")}`);
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      const categoriesList = Array.isArray(business.product_categories)
        ? business.product_categories
        : business.product_category
        ? [business.product_category]
        : [];
      // business
      fd.append("brand_name", business.brand_name);
      fd.append("owner_name", business.owner_name);
      fd.append("mobile", business.mobile);
      fd.append("whatsapp", business.whatsapp || "");
      fd.append("email", business.email);
      fd.append("product_category", categoriesList[0] || "");
      fd.append("product_categories", JSON.stringify(categoriesList));
      fd.append("brand_story", business.brand_story || "");
      // address
      fd.append("address_line_1", value.address_line_1);
      fd.append("address_line_2", value.address_line_2 || "");
      fd.append("district", value.district);
      fd.append("state", value.state);
      fd.append("pin_code", value.pin_code.trim());
      fd.append("gst_registered", gstYes ? "true" : "false");
      fd.append("gstin", gstYes ? (value.gstin || "").trim().toUpperCase() : "");
      fd.append("pan_number", pan);
      if (resumeToken) fd.append("resume_token", resumeToken);
      fd.append("pan_document", panFile);
      fd.append("cheque_document", chequeFile);
      if (gstYes && gstFile) fd.append("gst_document", gstFile);

      const { data } = await api.post("/onboarding/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSubmitted(data);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full" data-testid="slide-4-address">
      <div className="eyebrow">Chapter 04 · Where You Are, Who You Are</div>
      <h2 className="mt-3 font-editorial text-[36px] sm:text-[44px] md:text-[54px] leading-[1.05] tracking-[-0.01em] max-w-3xl">
        <MaskedLines
          lines={[
            "Address, tax &",
            <span key="v" className="italic text-[color:var(--sbh-terracotta)]">verification.</span>,
          ]}
        />
      </h2>

      <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Address block */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
        >
          <div className="md:col-span-2 eyebrow text-[color:var(--sbh-ink-2)]">Address</div>
          <div className="md:col-span-2">
            <Field
              label="Address Line 1"
              required
              value={value.address_line_1}
              onChange={set("address_line_1")}
              testId="address-line-1"
            />
          </div>
          <div className="md:col-span-2">
            <Field
              label="Address Line 2"
              value={value.address_line_2}
              onChange={set("address_line_2")}
              testId="address-line-2"
            />
          </div>
          <Field
            label="District"
            required
            value={value.district}
            onChange={set("district")}
            testId="address-district"
          />
          <Field
            label="State"
            required
            value={value.state}
            onChange={set("state")}
            testId="address-state"
          />
          <Field
            label="Pin Code"
            required
            value={value.pin_code}
            onChange={set("pin_code")}
            testId="address-pin"
          />

          <div className="md:col-span-2 mt-6 eyebrow text-[color:var(--sbh-ink-2)]">
            Tax & Registration
          </div>

          <div className="md:col-span-2 pt-6 border-b border-[color:var(--sbh-border)] pb-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-editorial text-[20px] text-[color:var(--sbh-ink)]">
                  Are you GST registered?
                </div>
                <div className="text-[13px] text-[color:var(--sbh-muted)] mt-1">
                  We&apos;ll ask for your GSTIN only if yes.
                </div>
              </div>
              <div className="gst-toggle" data-testid="gst-toggle">
                <button
                  type="button"
                  className={gstYes ? "on" : ""}
                  onClick={() => set("gst_registered")(true)}
                  data-testid="gst-toggle-yes"
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={!gstYes ? "on" : ""}
                  onClick={() => set("gst_registered")(false)}
                  data-testid="gst-toggle-no"
                >
                  No
                </button>
              </div>
            </div>
          </div>

          {gstYes && (
            <div className="md:col-span-2">
              <Field
                label="GSTIN"
                required
                value={value.gstin}
                onChange={set("gstin")}
                testId="address-gstin"
              />
            </div>
          )}
          <div className="md:col-span-2">
            <Field
              label="PAN Number"
              required
              value={value.pan_number}
              onChange={set("pan_number")}
              testId="address-pan-number"
            />
          </div>
        </motion.div>

        {/* Documents block */}
        <motion.div
          className="flex flex-col gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
        >
          <div className="flex items-center gap-3">
            <Lock
              className="w-4 h-4 text-[color:var(--sbh-terracotta)]"
              strokeWidth={1.8}
            />
            <span className="eyebrow">Verification Documents</span>
          </div>
          <p className="text-[13.5px] leading-relaxed text-[color:var(--sbh-ink-2)] max-w-md">
            <em className="font-editorial not-italic">Why we ask —</em> these are required by law to list on a GST-compliant marketplace and help us verify your business. Your documents are stored securely and used only for verification.
          </p>

          <DropField
            label="Upload PAN Card"
            required
            file={panFile}
            onFile={setPanFile}
            testId="upload-pan"
          />
          <DropField
            label="Upload Cancelled Cheque or Bank Passbook (first page)"
            required
            file={chequeFile}
            onFile={setChequeFile}
            testId="upload-cheque"
          />
          {gstYes && (
            <DropField
              label="Upload GST Certificate"
              required
              file={gstFile}
              onFile={setGstFile}
              testId="upload-gst"
            />
          )}

          <div className="mt-2 text-[12px] leading-relaxed text-[color:var(--sbh-muted)] max-w-md border-l border-[color:var(--sbh-mustard)] pl-4">
            <span className="eyebrow block mb-1 text-[color:var(--sbh-ink-2)]">Note</span>
            We do <b>not</b> collect bank account number or IFSC in this form. Payout bank details are collected separately, only after your application is reviewed and approved &mdash; via a private secure link.
          </div>
        </motion.div>
      </div>

      <div className="mt-14 flex items-center justify-between gap-6">
        <button className="btn-ghost" onClick={onBack} data-testid="address-back-btn">
          <ArrowLeft className="w-4 h-4" strokeWidth={2} /> Back
        </button>
        <button
          className="btn-editorial"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="address-submit-btn"
        >
          {submitting ? "Submitting…" : "Submit Application"}
          <ArrowRight className="arrow w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
